import { Injectable } from '@angular/core';
import { IUser } from 'src/modules/authentication/interfaces/user.interface';
import { OnboardingStepEnum } from '../enums/onboarding-step.enum';
import { onboardingEnumsToRouteMap } from '../utils/helpers/onboarding-steps-route-map';

/**
 * Service to handle team-based onboarding flow logic
 */
@Injectable({
  providedIn: 'root'
})
export class TeamOnboardingUtilService {

  // Standard onboarding flow order
  private readonly ONBOARDING_FLOW_ORDER: OnboardingStepEnum[] = [
    OnboardingStepEnum.STAFFER_EXPERTISE,
    OnboardingStepEnum.CONTRACTOR_INFORMATION,
    OnboardingStepEnum.STAFFER_GETREADY,
    OnboardingStepEnum.STAFFER_PERSONAL_INFORMATION,
    OnboardingStepEnum.STAFFER_CONTRACT,
    OnboardingStepEnum.STAFFER_PROFESSIONAL_INFORMATION,
    OnboardingStepEnum.STAFFER_ONBOARDING_DASHBOARD,
    OnboardingStepEnum.STAFFER_CERTIFICATES,
    OnboardingStepEnum.STAFFER_VSS_ATTESTATION,
    OnboardingStepEnum.STAFFER_ADD_REFERENCE,
    OnboardingStepEnum.STAFFER_ADD_BACKGROUND_CHECK,
    OnboardingStepEnum.STAFFER_HOME
  ];

  constructor() { }

  /**
   * Determines if a specific onboarding step should be skipped based on team settings
   * @param user The user object with team onboarding settings
   * @param step The onboarding step to check
   * @returns true if the step should be skipped, false otherwise
   */
  shouldSkipOnboardingStep(user: IUser, step: OnboardingStepEnum): boolean {
    // If user didn't sign up via team invitation or has no team settings, don't skip any steps
    // Check for either pendingTeamInvitation (during onboarding) or restrictedToEntity (after approval)
    if ((!user?.pendingTeamInvitation && !user?.restrictedToEntity) || !user?.teamOnboardingSettings?.optionalOnboardingSteps) {
      return false;
    }

    const optionalSteps = user.teamOnboardingSettings.optionalOnboardingSteps;

    // Map onboarding steps to team settings
    switch (step) {
      case OnboardingStepEnum.STAFFER_ADD_BACKGROUND_CHECK:
      case OnboardingStepEnum.STAFFER_ADD_BACKGROUND_CHECK_CPY:
        return optionalSteps.backgroundCheck === true;

      case OnboardingStepEnum.STAFFER_CONTRACT:
        return optionalSteps.contractSigning === true;

      case OnboardingStepEnum.STAFFER_ADD_REFERENCE:
        return optionalSteps.referenceChecks === true;

      case OnboardingStepEnum.STAFFER_CERTIFICATES:
        return optionalSteps.certificates === true;

      default:
        return false;
    }
  }

  /**
   * Gets the list of required onboarding steps for a user based on team settings
   * @param user The user object with team onboarding settings
   * @param allSteps Array of all onboarding steps in order
   * @returns Filtered array of required onboarding steps
   */
  getRequiredOnboardingSteps(user: IUser, allSteps: OnboardingStepEnum[]): OnboardingStepEnum[] {
    if ((!user?.pendingTeamInvitation && !user?.restrictedToEntity) || !user?.teamOnboardingSettings?.optionalOnboardingSteps) {
      return allSteps;
    }

    return allSteps.filter(step => !this.shouldSkipOnboardingStep(user, step));
  }

  /**
   * Determines the next required onboarding step after the current step
   * @param user The user object with team onboarding settings
   * @param currentStep The current onboarding step
   * @param orderedSteps Array of all onboarding steps in order
   * @returns The next required step or null if all steps are complete
   */
  getNextRequiredStep(
    user: IUser,
    currentStep: OnboardingStepEnum,
    orderedSteps: OnboardingStepEnum[]
  ): OnboardingStepEnum | null {
    const currentIndex = orderedSteps.indexOf(currentStep);

    if (currentIndex === -1) {
      return null;
    }

    // Find the next step that is not skipped
    for (let i = currentIndex + 1; i < orderedSteps.length; i++) {
      if (!this.shouldSkipOnboardingStep(user, orderedSteps[i])) {
        return orderedSteps[i];
      }
    }

    return null; // All steps completed
  }

  /**
   * Checks if user signed up via team invitation
   * @param user The user object
   * @returns true if user signed up via team invitation
   */
  isTeamSignup(user: IUser): boolean {
    return !!(user?.signupSource?.team || user?.pendingTeamInvitation);
  }

  /**
   * Gets a summary of which onboarding steps are optional for the user
   * @param user The user object with team onboarding settings
   * @returns Object with step names and whether they're optional
   */
  getOptionalStepsSummary(user: IUser): {
    backgroundCheck: boolean,
    contractSigning: boolean,
    referenceChecks: boolean,
    certificates: boolean
  } {
    const defaults = {
      backgroundCheck: false,
      contractSigning: false,
      referenceChecks: false,
      certificates: false
    };

    if ((!user?.pendingTeamInvitation && !user?.restrictedToEntity) || !user?.teamOnboardingSettings?.optionalOnboardingSteps) {
      return defaults;
    }

    return {
      backgroundCheck: user.teamOnboardingSettings.optionalOnboardingSteps.backgroundCheck ?? false,
      contractSigning: user.teamOnboardingSettings.optionalOnboardingSteps.contractSigning ?? false,
      referenceChecks: user.teamOnboardingSettings.optionalOnboardingSteps.referenceChecks ?? false,
      certificates: user.teamOnboardingSettings.optionalOnboardingSteps.certificates ?? false
    };
  }

  /**
   * Gets the next onboarding route after completing the current step
   * Takes team settings into account to skip optional steps
   * @param user The user object with team onboarding settings
   * @param currentStep The step that was just completed
   * @param defaultNextRoute The default next route if no team settings apply
   * @returns The route to navigate to
   */
  getNextOnboardingRoute(user: IUser, currentStep: OnboardingStepEnum, defaultNextRoute: string): string {
    // If no team signup, use default route
    if (!this.isTeamSignup(user)) {
      return defaultNextRoute;
    }

    const nextStep = this.getNextRequiredStep(user, currentStep, this.ONBOARDING_FLOW_ORDER);

    if (nextStep) {
      return onboardingEnumsToRouteMap[nextStep] || defaultNextRoute;
    }

    // All required steps completed, go to dashboard
    return '/talent-dashboard/home';
  }

  /**
   * Checks if the current onboarding step should be displayed
   * Useful for conditional rendering in onboarding dashboard
   * @param user The user object with team onboarding settings
   * @param step The step to check
   * @returns true if the step should be shown, false if it should be hidden
   */
  shouldShowOnboardingStep(user: IUser, step: OnboardingStepEnum): boolean {
    return !this.shouldSkipOnboardingStep(user, step);
  }
}
